import {
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnInit,
    PLATFORM_ID,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {ApiArticle, ApiArticlesGetAllowedValues, ApiArticlesGetRequestParams} from "@api/articles";
import {distinctUntilChanged, map, switchMap, take, takeUntil} from "rxjs/operators";
import {select, Store} from "@ngrx/store";
import {getSelectedCustomer} from "@store/selectors/user.selector";
import {ArticleService} from "@iw-shop/article/services/article.service";
import {ConfigService} from "@iw-shop/config/service/config.service";
import {AppState} from "@store/state/app.state";
import {forkJoin, Observable, of, Subject, Subscription} from "rxjs";
import { v4 as uuidv4 } from 'uuid';

import {ChangeTimezone} from "@iw-shop/core/functions/date";
import {isPlatformBrowser} from "@angular/common";
import {ArticleRecommendationService} from "@iw-shop/article/services/article-recommandation.service";

@Component({
    selector: 'iw-shop-article-slider',
    standalone: false,
    templateUrl: './article-slider.component.html',
    styleUrls: ['./article-slider.component.scss']
})
export class ArticleSliderComponent implements OnInit{
    carouselId: string = '';
    platformId;
    private readonly fallbackRecommendationLimit = 10;
    private readonly minRecommendationCount = 10;

    carouselRef: any;
    @ViewChild('carousel', { read: ElementRef }) set carouselContent(content: ElementRef) {
        if (content && isPlatformBrowser(this.platformId)) {
            this.carouselRef = content;

            const observer = new MutationObserver((mutations) => {
                // Verhindert unnötige Aufrufe, wenn nur Tabindex geändert wurde
                const isOurChange = mutations.every(m => m.attributeName === 'tabindex' || m.attributeName === 'aria-label');
                if (!isOurChange) {
                    this.WCAGCleaner();
                }
            });

            observer.observe(content.nativeElement, {
                attributes: true,
                childList: true,
                subtree: true,
                attributeFilter: ['class', 'style'] // Beobachte nur relevante Änderungen
            });

            // Initialer Call mit minimalem Delay, damit PrimeNG fertig ist
            setTimeout(() => this.WCAGCleaner(), 100);
        }
    }
    @Input() set without_article_numbers_json(without_article_numbers_json: string[] | string) {
        if (typeof without_article_numbers_json === 'string') {
            try {
                this.withoutArticleNumbers = JSON.parse(without_article_numbers_json);
            } catch {}
        } else if (Array.isArray(without_article_numbers_json)) {
            this.articleNumbers = without_article_numbers_json;
        }
    }
    @Input() withoutArticleNumbers: string[] = [];
    @Input() set article_numbers_json(article_numbers_json: string[] | string) {
        if (typeof article_numbers_json === 'string') {
            try {
                this.articleNumbers = JSON.parse(article_numbers_json);
            } catch {}
        } else if (Array.isArray(article_numbers_json)) {
            this.articleNumbers = article_numbers_json;
        }
    }
    @Input() articleNumbers?: string[];
    @Input() notifications_json?: string;
    @Input() notifications?: string[];

    @Input() headline?: string;
    @Input() sort_json?: string;
    @Input() sort?: {
        field: ApiArticlesGetAllowedValues['sortFields'];
        direction: 1 | -1;
    }[];

    @Input() sortByPassedArticleNumbers_json?: string;
    @Input() sortByPassedArticleNumbers?: string[];

    @Input() sort_individual?: string;
    @Input() offer_prices?: string;
    @Input() recommandationsByCart?: boolean;
    @Input() cartRecommendationFallbackSortIndividual?: string ='Beliebtheit';

    destroy$: Subject<boolean> = new Subject<boolean>();

    articles: ApiArticle[] = [];
    articleNumbersTemp?: string[];
    articleRequestParams: ApiArticlesGetRequestParams = {};


    customerNumber?: string;

    carouselArticleRecommendationsOptions = [
        {
            breakpoint: '1200px',
            numVisible: 4,
            numScroll: 4
        },
        {
            breakpoint: '1024px',
            numVisible: 3,
            numScroll: 3
        },
        {
            breakpoint: '768px',
            numVisible: 2,
            numScroll: 2
        },
        {
            breakpoint: '560px',
            numVisible: 1,
            numScroll: 1
        }
    ];

    loading: boolean = false;
    $getArticles?: Subscription;

    constructor(
        protected articleService: ArticleService,
        private articleRecommendationService: ArticleRecommendationService,
        private configService: ConfigService,
        private store: Store<AppState>,
        @Inject(PLATFORM_ID) platformId: Object
    ) {
        this.platformId = platformId;
    }

    ngOnInit(): void {
        this.carouselId = uuidv4();
        this.articleService.setAvailabilities(this.configService.getAvailabilities());
        this.articleRequestParams = this.configService.getDefaultArticleListRequestParams();
        if(this.articleRequestParams.articles?.groupBy){
            this.articleRequestParams.articles.groupBy = undefined;
        }
        this.store.select(getSelectedCustomer).pipe(take(1)).subscribe(r => {
            if (r?.customerNumber) {
                this.customerNumber = r.customerNumber;
            }
        })
        this.store
            .pipe(
                takeUntil(this.destroy$),
                select(getSelectedCustomer),
                distinctUntilChanged((prev, curr) => {
                    return prev?.customerNumber === curr?.customerNumber;

                }),
            )
            .subscribe(
                (r) => {
                    if (r?.customerNumber != this.customerNumber) {
                        this.customerNumber = r?.customerNumber ?? undefined;
                        this.articleRequestParams = this.configService.getDefaultArticleListRequestParams(this.articleRequestParams);
                        this.loadArticles();
                    }

                }
            );
        this.loadArticles();
    }

    ngOnChanges(){
        //this.loadArticles();
    }

    WCAGCleaner(){
        if (!this.carouselRef) return;
        const root: HTMLElement = this.carouselRef.nativeElement;

        // 1. Buttons fixen
        // Wir suchen tiefer: PrimeNG nutzt oft spezifische Klassen wie .p-carousel-prev
        const navButtons = root.querySelectorAll('.p-carousel-prev, .p-carousel-next, p-button button');

        navButtons.forEach(btn => {
            const isNext = btn.classList.contains('p-carousel-next') || btn.getAttribute('aria-label') === 'Next';
            btn.setAttribute('aria-label', isNext ? 'Nächste Seite' : 'Vorherige Seite');
            // Entferne aria-label vom Wrapper (p-button), falls vorhanden
            btn.parentElement?.removeAttribute('aria-label');
        });

        // 2. Tab-Index für versteckte Slides korrigieren
        // PrimeNG setzt für nicht sichtbare Slides oft [aria-hidden="true"]
        const hiddenElements = root.querySelectorAll('[aria-hidden="true"]');

        hiddenElements.forEach(container => {
            // Alle fokussierbaren Elemente innerhalb eines versteckten Containers deaktivieren
            const focusables = container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            focusables.forEach(el => {
                el.setAttribute('tabindex', '-1');
            });

            // Falls der Container selbst ein Button/Link ist
            if (container.matches('button, a, [tabindex]')) {
                container.setAttribute('tabindex', '-1');
            }
        });
    }

    loadArticles() {
        if (this.$getArticles) {
            this.$getArticles.unsubscribe();
        }
        this.loading = true;
        this.articleRequestParams = this.configService.getDefaultArticleListRequestParams(this.articleRequestParams);
        const date = ChangeTimezone(new Date(), 'Europe/Berlin');
        if (!this.articleRequestParams.articles) {
            this.articleRequestParams.articles = {};
        }

        if (this.notifications_json) {
            this.notifications = JSON.parse(this.notifications_json);
        }
        if (this.notifications) {
            if (this.articleRequestParams.filters &&
                this.articleRequestParams.filters.length > 0) {
                this.articleRequestParams.filters.push({
                    field: 'notifications.category',
                    value: "^(" +this.notifications.join('|') + ")$",
                    useRegex: 1
                });
            } else {
                this.articleRequestParams.filters = [{
                    field: 'notifications.category',
                    value: "^(" +this.notifications.join('|') + ")$",
                    useRegex: 1
                }];
            }
            this.articleRequestParams.filterDates = [
                {
                    fields: ['notifications.validFrom'],
                    lessThanEqual: date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate() + ' 00:00:00'
                },
                {
                    fields: ['notifications.validTo'],
                    greaterThanEqual: date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate() + ' 23:59:59'
                }
            ];
        }

        if(this.offer_prices == 'true'){
            this.articleRequestParams.filterDatesArticlePrices = [
                {
                    fields: ['validFrom'],
                    lessThanEqual: date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate() + ' 00:00:00'
                },
                {
                    fields: ['validTo'],
                    greaterThanEqual: date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate() + ' 23:59:59'
                }
            ]
        }

        if (this.sort_json) {
            this.sort = JSON.parse(this.sort_json);
        }
        if (this.sort) {
            this.articleRequestParams.articles.sort = this.sort;
        }
        if (this.sort_individual) {
            this.articleRequestParams.articles.sortIndividual = this.sort_individual;
        }
        if (this.sortByPassedArticleNumbers_json) {
            this.sortByPassedArticleNumbers = JSON.parse(this.sortByPassedArticleNumbers_json);
        }
        if (this.sortByPassedArticleNumbers) {
            this.articleRequestParams.articles.sortByPassedArticleNumbers = this.sortByPassedArticleNumbers;
        }

        const cartRecommendationFallbackSortIndividual = this.cartRecommendationFallbackSortIndividual?.trim();

        const articleRequest$ = this.recommandationsByCart
            ? this.getCartRecommendationContext().pipe(
                switchMap(({cartArticleNumbers, recommendationArticleNumbers}) => {
                    if (recommendationArticleNumbers.length === 0) {
                        return cartRecommendationFallbackSortIndividual
                            ? this.getFallbackArticles(cartArticleNumbers, this.minRecommendationCount, cartRecommendationFallbackSortIndividual)
                            : of([] as ApiArticle[]);
                    }

                    const visibleRecommendationNumbers = recommendationArticleNumbers.slice(0, this.minRecommendationCount);
                    return this.getArticlesByArticleNumbers(visibleRecommendationNumbers).pipe(
                        switchMap(recommendedArticles => {
                            if (recommendedArticles.length >= this.minRecommendationCount || !cartRecommendationFallbackSortIndividual) {
                                return of(recommendedArticles.slice(0, this.minRecommendationCount));
                            }

                            const excludedArticleNumbers = [
                                ...new Set([
                                    ...cartArticleNumbers,
                                    ...recommendedArticles.map(article => article.articleNumber)
                                ])
                            ];

                            return this.getFallbackArticles(
                                excludedArticleNumbers,
                                this.minRecommendationCount - recommendedArticles.length,
                                cartRecommendationFallbackSortIndividual
                            ).pipe(
                                map(fallbackArticles => [...recommendedArticles, ...fallbackArticles].slice(0, this.minRecommendationCount))
                            );
                        })
                    );
                })
            )
            : this.articleService.get(this.articleRequestParams).pipe(
                map(r => r.result?.articles?.data ?? [])
            );

        this.$getArticles = articleRequest$.subscribe(
            {
                next: (articles) => {
                    this.articles = articles ?? [];
                    this.loading = false;
                    this.articleNumbersTemp = this.articleNumbers;
                    this.WCAGCleaner()
                },
                error: () => {
                    this.loading = false;
                    this.articleNumbersTemp = this.articleNumbers;
                }
            }
        );
    }

    private getCartRecommendationContext(): Observable<{
        cartArticleNumbers: string[];
        recommendationArticleNumbers: string[];
    }> {
        return this.store.select(state => state.shoppingCart.items?.articles ?? []).pipe(
            take(1),
            map(articles => [...new Set(articles.map(article => article.articleNumber).filter(Boolean))]),
            switchMap(cartArticleNumbers => {
                if (cartArticleNumbers.length === 0) {
                    return of({
                        cartArticleNumbers,
                        recommendationArticleNumbers: [] as string[]
                    });
                }

                return forkJoin(
                    cartArticleNumbers.map(articleNumber =>
                        this.articleRecommendationService.get(articleNumber).pipe(
                            map(response =>
                                response.result?.articleRecommendations?.data?.[0]?.recommendations ?? []
                            )
                        )
                    )
                ).pipe(
                    map(results => {
                        const flattened = results.flat().filter(Boolean);
                        const cartArticleSet = new Set(cartArticleNumbers);
                        const filtered = flattened.filter(articleNumber => !cartArticleSet.has(articleNumber));
                        return {
                            cartArticleNumbers,
                            recommendationArticleNumbers: [...new Set(filtered)]
                        };
                    })
                );
            })
        );
    }

    private getArticlesByArticleNumbers(articleNumbers: string[]): Observable<ApiArticle[]> {
        const requestParams = this.cloneArticleRequestParams();
        this.applyArticleNumberFilterToParams(requestParams, articleNumbers, true);
        requestParams.articles!.sortByPassedArticleNumbers = articleNumbers;
        requestParams.articles!.limit = Math.max(articleNumbers.length, 1);
        requestParams.articles!.start = 0;
        return this.articleService.get(requestParams).pipe(
            map(response => response.result?.articles?.data ?? [])
        );
    }

    private getFallbackArticles(
        excludedArticleNumbers: string[],
        requiredCount: number,
        fallbackSortIndividual: string,
        start: number = 0,
        accumulated: ApiArticle[] = []
    ): Observable<ApiArticle[]> {
        if (requiredCount <= 0) {
            return of([] as ApiArticle[]);
        }

        const requestParams = this.cloneArticleRequestParams();
        requestParams.articles!.start = start;
        requestParams.articles!.limit = this.fallbackRecommendationLimit;
        requestParams.articles!.sortIndividual = fallbackSortIndividual;
        delete requestParams.articles!.sortByPassedArticleNumbers;
        this.applyArticleNumberFilterToParams(requestParams, undefined, false);

        return this.articleService.get(requestParams).pipe(
            map(response => response.result?.articles?.data ?? []),
            switchMap(page => {
                const excludedSet = new Set(excludedArticleNumbers);
                const accumulatedSet = new Set(accumulated.map(article => article.articleNumber));
                const filteredPage = page.filter(article =>
                    !excludedSet.has(article.articleNumber) && !accumulatedSet.has(article.articleNumber)
                );
                const nextAccumulated = [...accumulated, ...filteredPage];

                if (nextAccumulated.length >= requiredCount || page.length < this.fallbackRecommendationLimit) {
                    return of(nextAccumulated.slice(0, requiredCount));
                }

                return this.getFallbackArticles(
                    excludedArticleNumbers,
                    requiredCount,
                    fallbackSortIndividual,
                    start + this.fallbackRecommendationLimit,
                    nextAccumulated
                );
            })
        );
    }

    private cloneArticleRequestParams(): ApiArticlesGetRequestParams {
        return JSON.parse(JSON.stringify(this.articleRequestParams)) as ApiArticlesGetRequestParams;
    }

    private applyArticleNumberFilterToParams(
        requestParams: ApiArticlesGetRequestParams,
        articleNumbers?: string[],
        strict: boolean = false
    ): void {
        if (!requestParams.filters) {
            requestParams.filters = [];
        }

        requestParams.filters = requestParams.filters.filter(filter => filter.field !== 'articleNumber');

        if (!articleNumbers || articleNumbers.length === 0) {
            if (strict) {
                requestParams.filters.push({
                    field: 'articleNumber',
                    value: '^$',
                    useRegex: 1
                });
            }
            return;
        }

        requestParams.filters.push({
            field: 'articleNumber',
            value: "^(" + articleNumbers.join('|') + ")$",
            useRegex: 1
        });
    }

    private applyArticleNumberFilter(articleNumbers?: string[], strict: boolean = false): void {
        this.applyArticleNumberFilterToParams(this.articleRequestParams, articleNumbers, strict);
    }


    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
